/**
 * Lazy avatar-image cache for the canvas scene (WI #5531).
 *
 * Banner avatars are <img>s loaded from blob URLs. We cache them by URL and
 * trigger a content repaint when one finishes loading, so the banner's initials
 * disc swaps to the image without any per-frame work.
 */
export class ImageCache {
  private readonly cache = new Map<string, HTMLImageElement>();

  /** `onLoad` is called (markDirty) when a newly requested image finishes loading. */
  constructor(private readonly onLoad: () => void) {}

  /**
   * Return the loaded <img> for `url`, or null while it's still loading. The
   * first request kicks off the fetch and repaints content on completion.
   */
  get(url: string): HTMLImageElement | null {
    const cached = this.cache.get(url);
    if (cached) {
      return cached.complete && cached.naturalWidth > 0 ? cached : null;
    }
    const img = new Image();
    img.onload = (): void => this.onLoad();
    // A failed/expired blob URL must be evicted, not cached as a permanently
    // non-`complete` image — otherwise `get` returns null forever and the fetch
    // never retries (the initials disc would never swap). Dropping the entry lets
    // the next frame re-request it.
    img.onerror = (): void => {
      this.cache.delete(url);
    };
    img.src = url;
    this.cache.set(url, img);

    return null;
  }

  /** Drop all cached bitmaps (called on renderer destroy so they can be GC'd). */
  clear(): void {
    this.cache.clear();
  }
}
